import { useEffect, useMemo } from 'react';

import { FlowRun } from 'new/components/monitor/Runs/components/FlowRunsTable/types';
import useQueryParams from 'new/hooks/useQueryParams';
import { useAppAdapter } from 'new/state/app/adapter/useAppAdapter';
import useEntities from 'new/state/entities/repository/useEntities';
import {
  useMetricsAdapter,
  UseMetricsAdapterReturnedType,
} from 'new/state/metrics/adapter/useMetricsAdapter';
import { useMetricsRepository } from 'new/state/metrics/repository/useMetricsRepository';
import usePaging from 'new/state/paging/repository/usePaging';

interface TableState {
  data: FlowRun[];
  meta: {
    currentPage: number;
    totalCount: number;
    pageCount: number;
    totalPageCount: number;
  };
  error: boolean | null;
  loading: boolean;
  lastUpdated: number | null;
}

interface TableActions {
  refresh: UseMetricsAdapterReturnedType<'requestFlowRuns'>;
}

export type UseFlowRunsTableReturnedType = [TableState, TableActions];

function useFlowRunsTable(): [TableState, TableActions] {
  const { getMetaInfoById } = usePaging();
  const { getRunsMetrics } = useMetricsRepository();
  const { requestFlowRuns } = useMetricsAdapter();
  const { setCurrentPage, setTotalPages, setTotalItems } = useAppAdapter();
  const { getSourceByFlowId, getFlowNodeById, getVendorById } = useEntities();

  const { page = 1 } = useQueryParams();
  const runsMetrics = getRunsMetrics();
  const fetchedFlowsId = runsMetrics?.flowIds ?? [];
  const metricsMap = runsMetrics?.metricsMappedByOriginNodeId;

  const flowsIdKey = useMemo(() => fetchedFlowsId.join(','), [fetchedFlowsId]);
  const metricsMapKey = useMemo(() => JSON.stringify(metricsMap), [metricsMap]);

  const flows = useMemo(
    () =>
      fetchedFlowsId
        .map((id: any) => {
          const flowNode = getFlowNodeById(id);
          if (!flowNode) return null;

          // CRITICAL: Use origin_node_id for source lookup, not the flow ID directly.
          // This is because:
          // 1. Sources have flow_node_id pointing to the ROOT flow node
          // 2. All flows in a tree share the same origin_node_id (the root's ID)
          // 3. If 'id' is a child flow ID, getSourceByFlowId(id) would fail
          // 4. Using origin_node_id ensures we find the source regardless of whether
          //    'id' is a root or child flow node
          const originNodeId = flowNode.origin_node_id ?? id;
          const source = getSourceByFlowId(originNodeId);
          if (!source) return null;

          const vendor = getVendorById(source.vendor_id);
          const meta = getMetaInfoById(source.connector_type);

          return {
            ...flowNode,
            vendor_id: source.vendor_id,
            source_type: source.source_type,
            connector_type: source.connector_type,
            icon: vendor?.small_logo || meta?.iconSm,
            connection_type: source.connection_type,
          };
        })
        .filter(Boolean) as FlowRun[],
    [flowsIdKey, getFlowNodeById, getSourceByFlowId, getVendorById, getMetaInfoById],
  );

  const flowsWithMetrics = useMemo(
    () =>
      flows.map((flow) => ({
        ...flow,
        metrics: metricsMap?.[flow.origin_node_id] ?? {},
      })),
    [metricsMapKey, flows],
  );

  useEffect(() => {
    requestFlowRuns({ page: Number(page) }).catch(console.error);
  }, []);

  useEffect(() => {
    if (runsMetrics?.meta) {
      setCurrentPage(runsMetrics?.meta?.currentPage ?? 1);
      setTotalPages(runsMetrics?.meta?.pageCount ?? 1);
      setTotalItems(runsMetrics?.meta?.totalCount ?? 0);
    }
  }, [runsMetrics?.meta, setCurrentPage, setTotalPages, setTotalItems]);

  return [
    {
      data: flowsWithMetrics,
      meta: runsMetrics?.meta as TableState['meta'],
      error: runsMetrics?.error ?? null,
      loading: runsMetrics?.loading ?? false,
      lastUpdated: runsMetrics?.lastUpdated,
    },
    { refresh: requestFlowRuns },
  ];
}

export { useFlowRunsTable };
